import React, { ReactNode } from 'react';
import AlertbarWrapper from '@/components/AlertbarWrapper';
import { Image } from '@cwlib/react-lib/lib/Image';
import { Icon } from '@cwlib/react-lib/lib/Icon';
import { Carousel } from '@/components/Carousel';
import useDocumentTitle from '@/hooks';

import './AuthPageLayout.scss';
import logo from '@/assets/img/logo.svg';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import CarouselSlideOnboard from '@/assets/img/login-carousel-onboard.png';
import CarouselSlideCollect from '@/assets/img/login-carousel-collect.png';


type AuthPageLayoutProps = {
  title: string;
  children: ReactNode;
  centered?: boolean;
};

/**
 * AuthPageLayout component
 * Shared layout for authentication-related pages (Login, PasswordReset, etc.)
 * Provides consistent structure with logo, form area, and optional marketing carousel
 *
 * @param props.title - Document title for the page
 * @param props.children - Form component to display in the main content area
 * @returns JSX element containing the complete auth page layout
 */
const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({
  title,
  children,
  centered = false
}) => {
  useDocumentTitle(title);

  return (
    <div
      className={`login-wrapper${centered ? ' login-wrapper--centered' : ''}`}
    >
      <AlertbarWrapper />
      <div className="logo-wrapper">
        <Image
          src={logo}
          alt="logo"
          width={centered ? '12.3rem' : '14rem'}
        />
      </div>
      <div
        className={`container-fluid login-content${
          centered ? ' login-content--centered' : ''
        }`}
      >
        <div className="row">
          <div
            className={`col-xl-5 col-md-6 col-sm-12 col-xs-12 align-self-start login-lhs${
              centered ? ' login-lhs--centered' : ''
            }`}
          >
            <div className="loginform-wrapper">
              {children}
            </div>
          </div>
          {!centered && <div className="col-xl-7 col-md-6 d-none d-md-block login-rhs">
            <Carousel interval={4000} touch>
              <Carousel.Item>
                <div className="carousel-slide-image-container">
                  <Image
                    src={CarouselSlideOnboard}
                    width="100%"
                    alt="Carousel slide Onboard"
                  />
                </div>
                <Carousel.Caption>
                  <div className="carousel-slide-title">
                    <div className="carousel-slide-title-hero">
                      Onboard quickly, trade confidently
                    </div>
                  </div>
                  <p className="carousel-slide-description">
                    Simplify credit decisions and approve customers faster
                    with powerful automation. Onboard combines AI-powered
                    credit scores, decision rules and software integrations to
                    assess risk and reduce manual processes.
                  </p>
                  <a
                    className="carousel-slide-cta-link"
                    href="https://creditorwatch.com.au/solutions/onboard"
                  >
                    <span>Explore Onboard</span>
                    <Icon icon={faArrowRight} />
                  </a>
                </Carousel.Caption>
              </Carousel.Item>
              <Carousel.Item>
                <div className="carousel-slide-image-container">
                  <Image
                    src={CarouselSlideCollect}
                    width="100%"
                    alt="Carousel slide Collect"
                  />
                </div>
                <Carousel.Caption>
                  <div className="carousel-slide-title">
                    <div className="carousel-slide-title-hero">
                      Streamline your collections process
                    </div>
                  </div>
                  <p className="carousel-slide-description">
                    Recover debt more efficiently with automated workflows and
                    escalation paths. Collect helps reduce overdue accounts
                    and improves collections rates with email reminders,
                    payment portal access and CreditorWatch-branded notices.
                  </p>
                  <a
                    className="carousel-slide-cta-link"
                    href="https://creditorwatch.com.au/solutions/collect"
                  >
                    <span>Explore Collect</span>
                    <Icon icon={faArrowRight} />
                  </a>
                </Carousel.Caption>
              </Carousel.Item>
            </Carousel>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default AuthPageLayout;